import {
  Box,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftAddon,
  Link,
  Select,
  Spinner,
  Text,
  Toast,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { IoPersonOutline } from 'react-icons/io5';
import buildingImg from '../assets/building.png';
import CustomButton from '../components/CustomButton';
import MerchantLogo from '../components/MerchantLogo';
import TermsAgreementModal from '../components/TermsAgreementModal';
import PalommaDisclaimer from '../components/svg/PalommaDisclaimer';
import AuthContext from '../contexts/AuthContext';
import MerchantContext from '../contexts/MerchantContext';
import { useBreakpointsContext } from '../hooks/useBreakpointsContext';
import { useTRPC } from '../trpc/client';
import { DocumentType, documentTypeNames, documentTypes } from '../types';
import { useQuery } from '@tanstack/react-query';

type Props = {
  // Set on the org login, which only shows the org's assets.
  org?: { id: string; logoUrl?: string; dashboardImageUrl?: string };
  merchantPicker?: React.ReactNode;
};

const AuthLoginContainer = ({ org, merchantPicker }: Props): JSX.Element => {
  const trpc = useTRPC();
  const { merchant } = useContext(MerchantContext);
  const [documentType, setDocumentType] = useState<DocumentType>(merchant?.erp === 'mobilia' ? 'cc' : undefined);
  const [user, setUser] = useState('');
  const { isSmallScreen } = useBreakpointsContext();
  const { login } = useContext(AuthContext);
  const [fetchingData, setFetchingData] = useState(false);
  const toast = useToast();
  const [termsModalDocumentNumber, setTermsModalDocumentNumber] = useState('');

  // The org login switches merchant after mount.
  useEffect(() => {
    setDocumentType(merchant?.erp === 'mobilia' ? 'cc' : undefined);
  }, [merchant?.erp]);

  const queryOptions = trpc.invoices.list.queryOptions({
    rentalsMerchantId: merchant?.id ?? '',
    customerIdentifier: user.trim(),
    customerDocumentType: documentType,
  });

  const userQuery = useQuery({
    ...queryOptions,
    enabled: false,
  });

  const loginHandler = async () => {
    setFetchingData(true);
    const result = await userQuery.refetch();
    setFetchingData(false);

    if (result.data?.invoices.length) {
      if (merchant?.termsUrl && !result.data.customer?.termsAccepted)
        setTermsModalDocumentNumber(result.data.customer?.documentNumber ?? '');
      else login(user, documentType, result.data.customer?.documentNumber, org?.id);
    } else {
      toast({
        title: 'Error iniciando sesión',
        description:
          'No se encontraron facturas asociadas a la identificación ingresada. Si crees que es un error, comunícate con nuestro equipo de cartera.',
        status: 'error',
        duration: 6000,
        render: (props) => (
          <Box onClick={() => window.open(`https://wa.me/${merchant?.supportWhatsapp}`, '_blank')} cursor={'pointer'}>
            <Toast {...props} />
          </Box>
        ),
      });
    }
  };

  return (
    <Box minHeight="100vh" width="100vw" display="flex" position={'relative'}>
      <Box position="absolute" top={0} left={0} height="100%" width={isSmallScreen ? '100%' : '50%'}>
        <Image
          src={
            (org ? org.dashboardImageUrl : merchant?.dashboardImageUrl ?? merchant?.org?.dashboardImageUrl) ??
            buildingImg
          }
          height="100%"
          width="100%"
          objectFit="cover"
        />
      </Box>
      <VStack
        width={['auto', 'auto', '50%']}
        justifyContent={'space-evenly'}
        position={isSmallScreen ? 'absolute' : 'relative'}
        backgroundColor={isSmallScreen ? 'white' : 'transparent'}
        py={12}
        px={4}
        left={isSmallScreen ? '50%' : undefined}
        top={isSmallScreen ? '50%' : undefined}
        transform={isSmallScreen ? 'translate(-50%, -50%)' : undefined}
        borderRadius={isSmallScreen ? '16px' : undefined}
        shadow={isSmallScreen ? 'lg' : 'none'}
        ml={isSmallScreen ? 0 : '50%'}
      >
        {(!org || org.logoUrl) && (
          <MerchantLogo boxWidth={['238px', '238px', '350px']} boxHeight={['98px', '98px', '140px']} org={org} />
        )}
        <VStack gap={4} width={['325px', '325px', '380px']} p={4}>
          <VStack gap={2}>
            <Text fontWeight={500} fontSize={['1.75rem', '1.75rem', '2rem']} textAlign={'center'}>
              Bienvenido al Sistema de Recaudos
            </Text>
            <Text fontSize={['1rem', '1rem', '1.125rem']}>Realiza pagos de manera segura</Text>
          </VStack>
          <VStack gap={6}>
            <Text fontWeight={500} fontSize={['1.25rem', '1.25rem', '1.375rem']}>
              Validación de usuario
            </Text>
            {fetchingData ? (
              <Spinner />
            ) : (
              <VStack spacing={2} width="100%">
                {merchantPicker}
                {!merchant?.loginByContract && merchant?.erp === 'mobilia' && (
                  <Select
                    border={'1px solid #5D5C60'}
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value as DocumentType)}
                  >
                    {documentTypes.map((type) => (
                      <option key={type} value={type}>
                        {documentTypeNames[type]}
                      </option>
                    ))}
                  </Select>
                )}
                <InputGroup>
                  <InputLeftAddon fontWeight={600} color={'rentals.primary.btn'} border={'1px solid #5D5C60'}>
                    <Icon as={IoPersonOutline} />
                  </InputLeftAddon>
                  <Input
                    width={'100%'}
                    placeholder={merchant?.loginByContract ? 'Número de contrato' : 'Número de documento'}
                    border={'1px solid #5D5C60'}
                    onChange={(e) => setUser(e.target.value)}
                    value={user}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' && user && merchant) {
                        loginHandler();
                      }
                    }}
                  />
                </InputGroup>
              </VStack>
            )}
            <CustomButton
              width={'100%'}
              onClick={loginHandler}
              isDisabled={fetchingData || !merchant || !user.trim() || (merchant?.erp === 'mobilia' && !documentType)}
            >
              INICIAR SESIÓN
            </CustomButton>
          </VStack>
        </VStack>
        <PalommaDisclaimer />
        <Box display={'flex'} gap={2}>
          <Link textAlign={'center'} href="https://www.palomma.com/terminos-condiciones">
            Términos y condiciones
          </Link>
          <Link textAlign={'center'} href="https://www.palomma.com/politicas-de-privacidad">
            Políticas de privacidad{' '}
          </Link>
        </Box>
      </VStack>
      <TermsAgreementModal
        user={user}
        documentType={documentType}
        documentNumber={termsModalDocumentNumber}
        orgId={org?.id}
        onClose={() => setTermsModalDocumentNumber('')}
      />
    </Box>
  );
};

export default AuthLoginContainer;
